import {ChangeDetectionStrategy, Component, ElementRef, Inject, signal, viewChild} from "@angular/core";
import {Autentiseringsmetode, BenyttetAutentiseringsmetode, CriiptoIdTilbyder, CriiptoTokenInnloggingsSpesifikasjon, Reautentiseringsinformasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {fjernFeil, leggTilFeilDersom} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {CriiptoAutentiseringPopupKomponent, HåndterCriiptoAutentiseringRespons} from "../../komponenter/criipto-autentisering-popup/criipto-autentisering-popup";
import {benyttetAutentiseringsmetodeFraCriiptoIdTilbyder, criiptoIdTilbyderFraBenyttetAutentiseringsmetode} from "felles-ng2/modell/benyttetAutentiseringsmetodeModellHjelpere";
import {alleEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";

const debug = initDebugger('ReautentiseringDialogKomponent');

/**
 * Dialog for reautentisering av innlogget bruker
 */
@Component({
    selector: 'reautentisering-dialog',
    templateUrl: 'reautentisering-dialog.html',
    styleUrl: 'reautentisering-dialog.scss',
    imports: [
        TranslateModule,
        MatDialogTitle,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        ReactiveFormsModule,
        VisSpinnerDirektiv,
        MatFormFieldModule,
        MatInput,
        CriiptoAutentiseringPopupKomponent,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReautentiseringDialogKomponent implements HåndterCriiptoAutentiseringRespons {

    static vis(dialog: MatDialog, melding: string, autentiser: Autentiser): MatDialogRef<ReautentiseringDialogKomponent, ReautentiseringDialogRespons> {
        return dialog.open(ReautentiseringDialogKomponent, {data: {melding, autentiser} as DialogParametre, disableClose: true, ariaLabelledBy: 'reautentisering-dialog-tittel', minWidth: 400, minHeight: 350});
    }

    //populeres fra DialogParametre
    readonly melding: string;
    readonly autentiser: Autentiser;

    passordfelt = viewChild<ElementRef<HTMLInputElement>>('passordfelt');

    passordSkjema = new FormGroup({
        passord: new FormControl('', {nonNullable: true, validators:Validators.required})
    });
    brukerHarEidAktivert = false; //oppdateres av konstr
    aktiverteEidMetoder: CriiptoIdTilbyder[] = alleEnuminnslag(CriiptoIdTilbyder); //oppdateres av konstr
    //kunne vært byttet ut med 'this' i template
    criiptoResponsHandler: HåndterCriiptoAutentiseringRespons = this;

    eidMetodeValgt = signal(false);
    eidMatcherIkkeInnloggetBruker = signal(false);
    modus = signal<'passord' | 'eid'>('passord');
    arbeider = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<ReautentiseringDialogKomponent, ReautentiseringDialogRespons>,
                private kontekst: BrukerkontekstTilbyder) {
        this.melding = parametre.melding;
        this.autentiser = parametre.autentiser;

        this.passordSkjema.controls.passord.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(() => this.fjernFeilPåPassordfelt());

        var aktivAutentiseringsmetode = this.kontekst.autentisertBruker().aktivtTokenInfo.autentiseringsmetode;
        switch (aktivAutentiseringsmetode) {
            case BenyttetAutentiseringsmetode.bankidNorsk:
            case BenyttetAutentiseringsmetode.bankidSvensk:
                this.aktiverteEidMetoder = criiptoIdTilbyderFraBenyttetAutentiseringsmetode(aktivAutentiseringsmetode);
                this.reautentiserMedEid();
                break;
            default:
                this.reautentiserMedPassord();
        }
        this.brukerHarEidAktivert = this.kontekst.autentisertBruker().bruker.autentiseringsmetoder.includes(Autentiseringsmetode.criiptoOpenId);
    }

    reautentiserMedPassord() {
        this.modus.set('passord');

        //for å unngå at nettleser fyller ut passord automatisk - som medfører at brukernavn blir fylt inn i søkefeltet
        //(eller annet inputfelt som måtte være synlig i tillegg til passordfeltet) rendres passordfeltet først med type=text
        //for så etter 1 sekund å bli endret til type=password
        setTimeout(() => this.passordfelt()!.nativeElement.setAttribute('type', 'password'), 1000);
    }

    reautentiserMedEid() {
        this.modus.set('eid');
    }

    async passordAngitt() {
        const passordOk = await this.arbeider.utfør(() =>
          this.autentiser({metode: BenyttetAutentiseringsmetode.passord, passord: this.passordSkjema.value.passord!}));

        leggTilFeilDersom(!passordOk, 'ugyldigPassord', this.passordSkjema.controls.passord);
        if (!passordOk) return;
        this.lukkOgReturner();
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    //HåndterCriiptoAutentiseringRespons implementasjon

    async håndterCriiptoAutentiseringRespons(spesifikasjon: CriiptoTokenInnloggingsSpesifikasjon) {
        const autentiseringOk = await this.autentiser({metode: benyttetAutentiseringsmetodeFraCriiptoIdTilbyder(spesifikasjon.tilbyder), eid: {autentiseringskode: spesifikasjon.kode, tilbyder: spesifikasjon.tilbyder, guiModus: spesifikasjon.guiModus}})
        if (!autentiseringOk) {
            this.eidMatcherIkkeInnloggetBruker.set(true);
        }
    }

    vedCriiptoAutentiseringAvbrutt(): void {
        this.avbryt();
    }

    //private hjelpemetoder

    private lukkOgReturner() {
        this.dialogRef.close(true);
    }

    private fjernFeilPåPassordfelt() {
        fjernFeil(this.passordSkjema.controls.passord, 'ugyldigPassord')
    }
}

/** Returnerer sann dersom angitt autentiseringsinfo identifiserer aktiv bruker */
export type Autentiser = (autentiseringsinfo: Reautentiseringsinformasjon) => Promise<boolean>

export type ReautentiseringDialogRespons = boolean;

export type DialogParametre = {
    melding: string;
    autentiser: Autentiser;
}
